import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QuestionImage from "./components/QuestionImage";
import {
  Archive,
  ArchiveRestore,
  BarChart3,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock3,
  Download,
  Image as ImageIcon,
  LockKeyhole,
  Search,
  Sparkles,
  TrendingUp,
  Trophy,
} from "lucide-react";
import { auth } from "./firebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { DashboardHeader, DashboardSidebar } from "./Dashboard";
import McqResultSummary from "./components/McqResultSummary";
import SubmittedDrawingAnswer from "./components/SubmittedDrawingAnswer";
import {
  fetchStudentAssessmentResults,
  reconcileExpiredQuizAttempts,
  startOrResumeStudentQuizAttempt,
} from "./utils/studentAssessmentApi";
import { studentAssessmentEntryPath } from "./utils/studentAssessmentNavigation";
import {
  correctResultAnswer,
  isMultipleChoiceResult,
  selectedResultAnswer,
} from "./utils/resultPresentation";
import {
  attemptLabel,
  attemptProgression,
  finalPolicyLabel,
  groupResultAttempts,
  selectDetailedAttemptId,
} from "./utils/attemptResults";
import "./ViewMyScores.css";

export const resultGroupKey = (submission = {}) => {
  const quizId = String(submission.quizId || "").trim();
  if (quizId) return `quizId:${quizId}`;
  return `quizCode:${String(submission.quizCode || "NoCode").trim() || "NoCode"}`;
};

const safePercentage = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.min(100, number)) : 0;
};

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

export const buildStudentResultsCsv = (quizSubmissions = []) => {
  const header = [
    "Question", "Student Answer", "Correct Answer", "Mark Awarded",
    "Maximum Mark", "Feedback", "Submitted At",
  ];
  const rows = quizSubmissions.map((submission) => [
    submission.questionText,
    selectedResultAnswer(submission),
    submission.markAwarded === "Pending" ? "" : correctResultAnswer(submission),
    submission.markAwarded,
    submission.maxMark,
    submission.feedback,
    submission.submittedAt instanceof Date
      ? submission.submittedAt.toLocaleString()
      : String(submission.submittedAt || ""),
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
};

const ViewMyScores = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const expandedDetailsRef = useRef(null);
  const deepLinkHandledRef = useRef("");

  const [submissions, setSubmissions] = useState([]);
  const [finalResults, setFinalResults] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [feedbackAccess, setFeedbackAccess] = useState([]);
  const [selectedAttemptByGroup, setSelectedAttemptByGroup] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedQuizCode, setExpandedQuizCode] = useState(null);
  const [archivedQuizzes, setArchivedQuizzes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [startingReattemptKey, setStartingReattemptKey] = useState("");
  const [reattemptError, setReattemptError] = useState(null);
  const reattemptStartInFlight = useRef("");
  const [isDarkMode, setIsDarkMode] = useState(
    () => localStorage.getItem("darkMode") === "true"
  );

  const loadStudentResults = useCallback(async () => {
    const reconciliation = await reconcileExpiredQuizAttempts();
    // Open attempts the server could not finalize have an unknown status.
    const unavailableIds = new Set((reconciliation?.unavailable || []).map((entry) => entry.attemptId));
    const resultData = await fetchStudentAssessmentResults();
    setSubmissions(resultData.results);
    setFinalResults(resultData.finalResults);
    setAttempts(resultData.attempts.map((attempt) => (unavailableIds.has(attempt.id)
      ? { ...attempt, status: "unavailable" }
      : attempt)));
    setFeedbackAccess(resultData.feedbackAccess || []);
    return resultData;
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);
    loadStudentResults()
      .catch((error) => {
        console.error("Error fetching student submissions:", error);
        alert("Failed to load your test scores. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [loadStudentResults, user?.uid]);

  const groupedByQuiz = useMemo(() => submissions.reduce((groups, submission) => {
    const groupKey = resultGroupKey(submission);
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(submission);
    return groups;
  }, {}), [submissions]);

  const getQuizSummary = (quizSubmissions) => {
    const totalMarks = quizSubmissions.reduce(
      (total, submission) => total + (parseFloat(submission.markAwarded) || 0), 0
    );
    const totalMax = quizSubmissions.reduce(
      (total, submission) => total + (parseFloat(submission.maxMark) || 0), 0
    );
    const percentage = totalMax > 0 ? (totalMarks / totalMax) * 100 : null;
    const latestDate = quizSubmissions.reduce(
      (latest, submission) => submission.submittedAt > latest ? submission.submittedAt : latest,
      new Date(0)
    );
    const isMarked = quizSubmissions.every(
      (submission) => submission.markAwarded !== "Pending"
    );
    return { totalMarks, totalMax, percentage, latestDate, isMarked };
  };

  const finalByQuiz = useMemo(() => new Map(
    finalResults.map((item) => [item.quizId, item.finalResult])
  ), [finalResults]);

  const feedbackAccessByGroup = useMemo(() => new Map(
    feedbackAccess.map((access) => [resultGroupKey(access), access])
  ), [feedbackAccess]);

  const resultGroups = useMemo(() => Object.keys(groupedByQuiz).map((groupKey) => {
    const groupSubmissions = groupedByQuiz[groupKey];
    const quizId = String(groupSubmissions[0]?.quizId || "").trim();
    const serverAttempts = attempts.filter((attempt) => attempt.quizId === quizId);
    const recordsByAttempt = groupResultAttempts(groupSubmissions);
    const history = recordsByAttempt.map((attempt) => {
      const serverAttempt = serverAttempts.find(({ id }) => id === attempt.id);
      const summary = getQuizSummary(attempt.records);
      const outcome = serverAttempt?.attemptOutcome || (summary.isMarked && summary.totalMax > 0 ? {
        status: "complete", totalAwarded: summary.totalMarks, totalPossible: summary.totalMax,
        percentage: Math.round(summary.percentage * 100) / 100,
        achievedOutcomeId: null, achievedOutcomeLabel: null,
      } : { status: "pending" });
      return {
        ...attempt,
        status: serverAttempt?.status || "submitted",
        submittedAt: serverAttempt?.submittedAt || summary.latestDate,
        outcome,
      };
    });
    serverAttempts.forEach((serverAttempt) => {
      if (!history.some(({ id }) => id === serverAttempt.id)) history.push({
        id: serverAttempt.id, key: `attempt:${serverAttempt.id}`,
        attemptNumber: serverAttempt.attemptNumber, records: [], status: serverAttempt.status,
        submittedAt: serverAttempt.submittedAt, outcome: serverAttempt.attemptOutcome,
      });
    });
    history.sort((left, right) => left.attemptNumber - right.attemptNumber);
    const finalResult = quizId ? finalByQuiz.get(quizId) || null : null;
    const latestHistory = history.at(-1);
    const displayStatus = latestHistory?.status === "unavailable"
      ? "unavailable"
      : latestHistory?.status === "in_progress"
      ? "in_progress"
      : latestHistory && latestHistory.outcome?.status !== "complete"
        ? "awaiting"
        : "marked";
    const fallbackSummary = latestHistory?.outcome?.status === "complete" ? {
      totalMarks: latestHistory.outcome.totalAwarded,
      totalMax: latestHistory.outcome.totalPossible,
      percentage: latestHistory.outcome.percentage,
      latestDate: latestHistory.submittedAt || new Date(0),
      isMarked: true,
    } : getQuizSummary(latestHistory?.records || groupSubmissions);
    return {
      groupKey, quizId, submissions: groupSubmissions, attempts: history, finalResult, displayStatus,
      feedbackAccess: feedbackAccessByGroup.get(groupKey) || {
        status: "released", mode: "disabled", action: null, available: false,
        remaining: 0, reason: "legacy-policy-disabled", launchAccess: null,
      },
      quizCode: groupSubmissions[0]?.quizCode || "NoCode",
      summary: finalResult ? {
        totalMarks: finalResult.totalAwarded,
        totalMax: finalResult.totalPossible,
        percentage: finalResult.percentage,
        latestDate: latestHistory?.submittedAt || fallbackSummary.latestDate,
        isMarked: true,
      } : fallbackSummary,
    };
  }), [attempts, feedbackAccessByGroup, finalByQuiz, groupedByQuiz]);

  const groupForKey = (groupKey) => resultGroups.find((group) => group.groupKey === groupKey);
  const quizCodeForGroup = (groupKey) => groupForKey(groupKey)?.quizCode || "NoCode";
  const isArchivedGroup = (groupKey) =>
    archivedQuizzes.includes(groupKey) || archivedQuizzes.includes(quizCodeForGroup(groupKey));

  const normalizedSearch = search.trim().toLowerCase();
  const visibleGroups = resultGroups
    .filter(({ groupKey }) => showArchived || !isArchivedGroup(groupKey))
    .filter(({ summary, displayStatus }) => statusFilter === "all" ||
      (statusFilter === "marked" ? summary.isMarked : displayStatus === "awaiting"))
    .filter(({ quizCode, submissions: groupSubmissions }) => !normalizedSearch ||
      quizCode.toLowerCase().includes(normalizedSearch) ||
      groupSubmissions.some((submission) =>
        String(submission.questionText || "").toLowerCase().includes(normalizedSearch)
      ))
    .sort((left, right) => sortOrder === "oldest"
      ? left.summary.latestDate - right.summary.latestDate
      : right.summary.latestDate - left.summary.latestDate);

  const markedGroups = resultGroups.filter(({ summary }) => summary.isMarked);
  const awaitingGroups = resultGroups.filter(({ displayStatus }) => displayStatus === "awaiting").length;
  const averageScore = markedGroups.length
    ? markedGroups.reduce((total, { summary }) => total + (summary.percentage || 0), 0) / markedGroups.length
    : null;

  useEffect(() => {
    if (loading || submissions.length === 0) return;
    const params = new URLSearchParams(location.search);
    const requestedQuizId = String(params.get("quizId") || "").trim();
    const requestedQuizCode = String(params.get("quizCode") || "").trim();
    const requestedAttemptId = String(params.get("attemptId") || "").trim();
    const deepLinkKey = (requestedQuizId
      ? `quizId:${requestedQuizId}`
      : requestedQuizCode ? `quizCode:${requestedQuizCode}` : "") +
      (requestedAttemptId ? `:attempt:${requestedAttemptId}` : "");
    if (!deepLinkKey || deepLinkHandledRef.current === deepLinkKey) return;

    const matchingSubmission = requestedQuizId
      ? submissions.find((submission) => String(submission.quizId || "").trim() === requestedQuizId) ||
        (requestedQuizCode
          ? submissions.find((submission) => String(submission.quizCode || "").trim() === requestedQuizCode)
          : null)
      : submissions.find((submission) => String(submission.quizCode || "").trim() === requestedQuizCode);

    deepLinkHandledRef.current = deepLinkKey;
    if (!matchingSubmission) return;
    const matchingGroupKey = resultGroupKey(matchingSubmission);
    const matchingGroup = resultGroups.find(({ groupKey }) => groupKey === matchingGroupKey);
    setExpandedQuestions({});
    setExpandedQuizCode(matchingGroupKey);
    if (matchingGroup) setSelectedAttemptByGroup((current) => ({
      ...current,
      [matchingGroupKey]: selectDetailedAttemptId(
        matchingGroup.attempts, matchingGroup.finalResult, requestedAttemptId
      ),
    }));
    if (archivedQuizzes.includes(matchingGroupKey) || archivedQuizzes.includes(matchingSubmission.quizCode)) {
      setShowArchived(true);
    }
    setTimeout(() => expandedDetailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [archivedQuizzes, loading, location.search, resultGroups, submissions]);

  useEffect(() => {
    if (!user?.uid) return;
    const storedArchived = localStorage.getItem(`archived_quizzes_${user.uid}`);
    if (!storedArchived) return;
    try {
      setArchivedQuizzes(JSON.parse(storedArchived));
    } catch (error) {
      console.error("Error parsing archived quizzes", error);
      localStorage.setItem(`archived_quizzes_${user.uid}`, JSON.stringify([]));
    }
  }, [user]);

  useEffect(() => {
    const syncTheme = () => {
      const darkMode = localStorage.getItem("darkMode") === "true";
      setIsDarkMode(darkMode);
      document.body.classList.toggle("dark-mode", darkMode);
      document.documentElement.setAttribute("data-dark-mode", darkMode ? "true" : "false");
    };
    syncTheme();
    window.addEventListener("storage", syncTheme);
    return () => window.removeEventListener("storage", syncTheme);
  }, []);

  const toggleTheme = () => {
    const nextMode = !isDarkMode;
    localStorage.setItem("darkMode", String(nextMode));
    setIsDarkMode(nextMode);
    document.body.classList.toggle("dark-mode", nextMode);
    document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
  };

  const toggleExpand = (group) => {
    const { groupKey } = group;
    if (groupKey === expandedQuizCode) {
      setExpandedQuizCode(null);
      return;
    }
    setExpandedQuestions({});
    setExpandedQuizCode(groupKey);
    setSelectedAttemptByGroup((current) => ({
      ...current,
      [groupKey]: current[groupKey] || selectDetailedAttemptId(group.attempts, group.finalResult),
    }));
    setTimeout(() => expandedDetailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const toggleQuestion = (questionId) => {
    setExpandedQuestions((current) => ({ ...current, [questionId]: !current[questionId] }));
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections((current) => ({ ...current, [sectionKey]: !current[sectionKey] }));
  };

  const toggleArchiveQuiz = (groupKey) => {
    setArchivedQuizzes((current) => {
      const legacyCode = quizCodeForGroup(groupKey);
      const nextArchived = current.includes(groupKey) || current.includes(legacyCode)
        ? current.filter((key) => key !== groupKey && key !== legacyCode)
        : [...current, groupKey];
      if (user?.uid) {
        localStorage.setItem(`archived_quizzes_${user.uid}`, JSON.stringify(nextArchived));
      }
      return nextArchived;
    });
  };

  const downloadFeedback = (quizSubmissions, quizCode) => {
    const csvContent = buildStudentResultsCsv(quizSubmissions);
    const url = URL.createObjectURL(new Blob([csvContent], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `feedback_${quizCode}.csv`;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const launchReattempt = async (group) => {
    if (reattemptStartInFlight.current) return;
    const access = group.feedbackAccess?.launchAccess;
    if (!access) {
      setReattemptError({
        groupKey: group.groupKey,
        message: "This assessment can no longer be opened directly. Refresh the page or contact your teacher.",
      });
      return;
    }

    reattemptStartInFlight.current = group.groupKey;
    setStartingReattemptKey(group.groupKey);
    setReattemptError(null);
    const entryPath = studentAssessmentEntryPath(access);
    try {
      const attempt = await startOrResumeStudentQuizAttempt(access, group.quizId);
      if (attempt.mode !== "created" && attempt.mode !== "resumed") {
        throw new Error("A new reattempt is no longer available.");
      }
      navigate(entryPath, { replace: true });
    } catch (error) {
      if (error?.code === "learning-content-required") {
        navigate(entryPath, { replace: true });
        return;
      }
      setReattemptError({
        groupKey: group.groupKey,
        message: error?.message || "This reattempt is no longer available. Your results have been refreshed.",
      });
      try {
        await loadStudentResults();
      } catch (refreshError) {
        console.error("Error refreshing student results after reattempt denial:", refreshError);
      }
    } finally {
      reattemptStartInFlight.current = "";
      setStartingReattemptKey("");
    }
  };

  const renderFeedback = (feedback) => {
    if (feedback === "Pending") return <span>Pending</span>;
    let processed = String(feedback)
      .replace(/\.\s+/g, ".\n\n")
      .replace(/:\s+/g, ":\n")
      .replace(/\n?Score:\s*/gi, "\n\nScore: ")
      .replace(/^Feedback:\s*/i, "")
      .trim();
    let scoreValue = null;
    const scoreMatch = processed.match(/\n{1,2}Score:\s*(.+)$/i);
    if (scoreMatch) {
      scoreValue = scoreMatch[1].trim();
      processed = processed.replace(/\n{1,2}Score:\s*[^\n]+$/i, "").trim();
    }
    const paragraphs = processed.split(/\n{2,}/)
      .map((paragraph) => paragraph.replace(/\n/g, " ").trim()).filter(Boolean);
    return (
      <div className="feedback-parsed">
        {paragraphs.map((paragraph, index) => (
          <p key={index} className="feedback-paragraph">{paragraph}</p>
        ))}
        {scoreValue !== null && (
          <div className="feedback-score-row">
            <span className="feedback-score-label">Score</span>
            <span className="feedback-score-value">{scoreValue}</span>
          </div>
        )}
      </div>
    );
  };

  const username = user?.displayName || user?.email?.split("@")[0] || "Student";

  const handleLogout = async () => {
    await auth.signOut();
    navigate("/");
  };

  const ScoreRing = ({ value, pending }) => {
    const progress = safePercentage(value);
    return (
      <div className={`vms-score-ring${pending ? " is-pending" : ""}`}
        style={{ "--score-progress": `${progress * 3.6}deg` }}>
        <div><strong>{pending ? "—" : `${Math.round(progress)}%`}</strong><span>{pending ? "Pending" : "Score"}</span></div>
      </div>
    );
  };

  const QuizCard = ({ group }) => {
    const { groupKey, quizCode, submissions: groupSubmissions, summary } = group;
    const isArchived = isArchivedGroup(groupKey);
    const isExpanded = expandedQuizCode === groupKey;
    return (
      <article className={`quiz-card ${summary.isMarked ? "result-marked" : "result-pending"}${isArchived ? " archived" : ""}`}>
        <div className="quiz-card-topline">
          <span className={`vms-status-pill ${group.displayStatus === "marked" ? "is-marked" : "is-pending"}`}>
            {group.displayStatus === "marked" ? <CheckCircle2 size={14} /> : <Clock3 size={14} />}
            {group.attempts.at(-1)?.status === "expired" ? "Time up" : group.displayStatus === "unavailable" ? "Status unavailable" : group.displayStatus === "in_progress" ? "Attempt in progress" : group.displayStatus === "awaiting" ? "Awaiting marking" : "Marked"}
          </span>
          {isArchived && <span className="archive-indicator">Archived</span>}
        </div>
        <div className="quiz-card-main">
          <ScoreRing value={summary.percentage} pending={!summary.isMarked} />
          <div className="quiz-card-copy">
            <span className="quiz-card-kicker">Assessment result</span>
            <h3 className="quiz-title">Test {quizCode}</h3>
            <p>{group.attempts.length} attempt{group.attempts.length === 1 ? "" : "s"}</p>
            <div className="quiz-card-meta">
              <span><Clock3 size={14} />{summary.latestDate.toLocaleDateString()}</span>
              <span><Trophy size={14} />{summary.isMarked
                ? summary.totalMarks !== null && summary.totalMax !== null
                  ? `${summary.totalMarks}/${summary.totalMax} marks`
                  : finalPolicyLabel(group.finalResult?.policy)
                : "Marking in progress"}</span>
              {group.finalResult?.achievedOutcomeLabel && <span className="vms-card-outcome">{group.finalResult.achievedOutcomeLabel}</span>}
              {group.feedbackAccess.action === "required" && (
                <span className="vms-card-reattempt">Reattempt required</span>
              )}
            </div>
          </div>
        </div>
        <div className="vms-card-actions">
          <button type="button" onClick={() => toggleExpand(group)}
            className="vms-card-action vms-card-action--primary" aria-expanded={isExpanded}>
            {isExpanded ? "Hide Details" : "View Details"}
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {group.feedbackAccess.status === "released" && (
            <button type="button" onClick={() => downloadFeedback(
              group.attempts.find(({ id }) => id === selectedAttemptByGroup[groupKey])?.records || groupSubmissions,
              quizCode
            )}
              className="vms-card-action vms-card-action--secondary"><Download size={16} />Download CSV</button>
          )}
          <button type="button" onClick={() => toggleArchiveQuiz(groupKey)}
            className={`vms-card-action vms-card-action--tertiary ${isArchived ? "unarchive" : "archive"}`}>
            {isArchived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
            {isArchived ? "Unarchive" : "Archive"}
          </button>
        </div>
      </article>
    );
  };

  const expandedGroup = expandedQuizCode ? groupForKey(expandedQuizCode) : null;
  const selectedAttemptId = expandedGroup ? selectedAttemptByGroup[expandedQuizCode] ||
    selectDetailedAttemptId(expandedGroup.attempts, expandedGroup.finalResult) : null;
  const selectedAttempt = expandedGroup?.attempts.find(({ id }) => id === selectedAttemptId) || null;
  const progression = expandedGroup ? attemptProgression(expandedGroup.attempts) : null;

  return (
    <div className={`sd-dashboard sd-brand-theme vms-page view-my-scores-page${isDarkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar role="student" username={username} onLogout={handleLogout} officialDashboardBrand
        open={sidebarOpen} onClose={() => setSidebarOpen(false)} activePath="/view-my-scores" />
      <div className="sd-workspace vms-workspace">
        <DashboardHeader username={username} role="student" darkMode={isDarkMode}
          onToggleTheme={toggleTheme} onOpenMenu={() => setSidebarOpen(true)}
          search={search} onSearch={setSearch} />
        <main className="sd-main vms-main">
          <section className="vms-hero">
            <div className="vms-hero-copy">
              <p className="vms-eyebrow">My results</p>
              <h1>My Assessment Results</h1>
              <p>Track your progress, review your feedback, and keep improving.</p>
              <button type="button" className="vms-assessments-link" onClick={() => navigate("/my-assessments")}>
                <ClipboardList size={16} />My Assessments
              </button>
            </div>
            <div className="vms-hero-art" aria-hidden="true">
              <span><BarChart3 size={31} /></span>
              <i className="vms-orbit vms-orbit-one"><Sparkles size={17} /></i>
              <i className="vms-orbit vms-orbit-two"><Trophy size={18} /></i>
              <i className="vms-orbit vms-orbit-three"><TrendingUp size={17} /></i>
            </div>
          </section>

          <section className="vms-metrics" aria-label="Results summary">
            <article className="vms-metric-card tone-blue"><span className="vms-metric-icon"><ClipboardList size={22} /></span><div><strong>{resultGroups.length}</strong><h2>Total Assessments</h2><p>All assessment results</p></div></article>
            <article className="vms-metric-card tone-green"><span className="vms-metric-icon"><CheckCircle2 size={22} /></span><div><strong>{markedGroups.length}</strong><h2>Marked Results</h2><p>Ready to review</p></div></article>
            <article className="vms-metric-card tone-orange"><span className="vms-metric-icon"><Clock3 size={22} /></span><div><strong>{awaitingGroups}</strong><h2>Awaiting Marking</h2><p>Feedback pending</p></div></article>
            <article className="vms-metric-card tone-purple"><span className="vms-metric-icon"><TrendingUp size={22} /></span><div><strong>{averageScore === null ? "—" : `${Math.round(averageScore)}%`}</strong><h2>Average Score</h2><p>Across marked results</p></div></article>
          </section>

          <section className="vms-results-panel">
            <div className="vms-results-heading">
              <div><h2>Your results</h2><p>{loading ? "Loading assessment results…" : `${visibleGroups.length} of ${resultGroups.length} assessments shown`}</p></div>
            </div>
            <div className="vms-filter-toolbar">
              <div className="vms-status-tabs" role="group" aria-label="Filter results by marking status">
                <button type="button" className={statusFilter === "all" ? "active" : ""}
                  onClick={() => setStatusFilter("all")}>All<span>{resultGroups.length}</span></button>
                <button type="button" className={statusFilter === "marked" ? "active" : ""}
                  onClick={() => setStatusFilter("marked")}>Marked<span>{markedGroups.length}</span></button>
                <button type="button" className={statusFilter === "pending" ? "active" : ""}
                  onClick={() => setStatusFilter("pending")}>Awaiting<span>{awaitingGroups}</span></button>
              </div>
              <div className="vms-filters">
                <label className="vms-search-field"><Search size={16} /><input value={search}
                  onChange={(event) => setSearch(event.target.value)} placeholder="Search results..."
                  aria-label="Search assessment results" /></label>
                <select value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} aria-label="Sort results">
                  <option value="newest">Newest first</option><option value="oldest">Oldest first</option>
                </select>
                <button type="button" className={`archive-toggle-label${showArchived ? " active" : ""}`}
                  onClick={() => setShowArchived((current) => !current)} aria-pressed={showArchived}>
                  {showArchived ? <ArchiveRestore size={15} /> : <Archive size={15} />}Show archived
                </button>
              </div>
            </div>

            {loading ? (
              <div className="loading-state" role="status"><div className="loading-spinner" /><p>Loading your results…</p></div>
            ) : visibleGroups.length ? (
              <div className="quiz-grid">{visibleGroups.map((group) => <QuizCard key={group.groupKey} group={group} />)}</div>
            ) : (
              <div className="empty-state"><Trophy size={30} /><h3>{resultGroups.length ? "No results match these filters" : "No results yet"}</h3><p>{resultGroups.length ? "Try another search or include archived assessments." : "Your completed assessment results will appear here."}</p></div>
            )}
          </section>

          {expandedGroup && (
            <section className="expanded-details" ref={expandedDetailsRef}>
              <div className="details-heading"><div><span className="vms-eyebrow">Detailed feedback</span><h2 className="details-title">Test {quizCodeForGroup(expandedQuizCode)}</h2></div><button type="button" onClick={() => setExpandedQuizCode(null)}>Hide details</button></div>
              {expandedGroup.finalResult && (
                <section className="vms-final-result" aria-label="Assessment result">
                  <div>
                    <span className="vms-result-label">Assessment result</span>
                    <strong>{expandedGroup.finalResult.percentage}%</strong>
                    {expandedGroup.finalResult.achievedOutcomeLabel && (
                      <span className="vms-outcome-chip">{expandedGroup.finalResult.achievedOutcomeLabel}</span>
                    )}
                  </div>
                  <div className="vms-final-meta">
                    {expandedGroup.finalResult.totalAwarded !== null && expandedGroup.finalResult.totalPossible !== null && (
                      <span>{expandedGroup.finalResult.totalAwarded} / {expandedGroup.finalResult.totalPossible} marks</span>
                    )}
                    <span><b>Result method:</b> {finalPolicyLabel(expandedGroup.finalResult.policy)}</span>
                    <span>{expandedGroup.finalResult.policy === "average"
                      ? `Average of ${expandedGroup.finalResult.completedAttemptCount} completed attempts`
                      : `${expandedGroup.finalResult.completedAttemptCount} completed attempt${expandedGroup.finalResult.completedAttemptCount === 1 ? "" : "s"}`}</span>
                    {progression && <span><b>Change from Attempt 1:</b> {progression.label}</span>}
                  </div>
                </section>
              )}

              <section className="vms-attempt-history" aria-labelledby="attempt-history-heading">
                <div className="vms-history-heading">
                  <div><span className="vms-eyebrow">Attempt history</span><h3 id="attempt-history-heading">Choose an attempt to review</h3></div>
                  {expandedGroup.attempts.some(({ status }) => status === "in_progress") && (
                    <span className="vms-active-attempt"><Clock3 size={14} />Attempt in progress</span>
                  )}
                </div>
                <div className="vms-attempt-list">
                  {expandedGroup.attempts.map((attempt) => {
                    const complete = attempt.outcome?.status === "complete";
                    const pending = !["in_progress", "unavailable"].includes(attempt.status) && !complete;
                    return (
                      <button key={attempt.id} type="button"
                        className={`vms-attempt-row${selectedAttemptId === attempt.id ? " selected" : ""}`}
                        aria-pressed={selectedAttemptId === attempt.id}
                        onClick={() => {
                          setExpandedQuestions({});
                          setSelectedAttemptByGroup((current) => ({ ...current, [expandedQuizCode]: attempt.id }));
                        }}>
                        <span><strong>{attemptLabel(attempt.attemptNumber)}</strong><small>{attempt.status === "expired" ? "Time up" : attempt.status === "unavailable" ? "Status unavailable" : attempt.status === "in_progress" ? "In progress" : pending ? "Marking in progress" : "Completed"}</small></span>
                        {complete && <span className="vms-attempt-score"><strong>{attempt.outcome.percentage}%</strong><small>{attempt.outcome.totalAwarded} / {attempt.outcome.totalPossible}</small></span>}
                        {complete && attempt.outcome.achievedOutcomeLabel && <span className="vms-attempt-outcome">{attempt.outcome.achievedOutcomeLabel}</span>}
                        <span className="vms-attempt-date">{attempt.submittedAt instanceof Date ? attempt.submittedAt.toLocaleDateString() : ""}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              {expandedGroup.feedbackAccess.status === "locked" && (
                <section className="vms-feedback-lock" role="status">
                  <span className="vms-feedback-lock-icon"><LockKeyhole size={22} /></span>
                  <div>
                    <span className="vms-eyebrow">{expandedGroup.feedbackAccess.action === "required"
                      ? "Assessment requirement not met"
                      : expandedGroup.feedbackAccess.action === "available"
                        ? "Another attempt is available"
                        : expandedGroup.feedbackAccess.reason === "quiz-locked"
                          ? "Assessment currently locked"
                        : "Assessment result pending"}</span>
                    <h3>{expandedGroup.feedbackAccess.action === "required"
                      ? "Reattempt required"
                      : expandedGroup.feedbackAccess.action === "available"
                        ? "Reattempt available"
                        : "Detailed feedback is currently locked"}</h3>
                    {expandedGroup.feedbackAccess.remaining > 0 && (
                      <strong>{expandedGroup.feedbackAccess.remaining} reattempt{expandedGroup.feedbackAccess.remaining === 1 ? "" : "s"} remaining</strong>
                    )}
                    <p>{expandedGroup.feedbackAccess.action === "required"
                      ? "Detailed feedback is currently locked. Your full feedback, mark scheme and example answers will become available when you meet the assessment requirement or complete all permitted attempts."
                      : expandedGroup.feedbackAccess.action === "available"
                        ? "Detailed feedback will become available once your permitted attempts are complete."
                        : expandedGroup.feedbackAccess.reason === "quiz-locked"
                          ? "Your teacher has locked this assessment. Detailed feedback remains locked while reattempt access is unavailable."
                        : "Your feedback will become available after marking and reattempt eligibility are resolved."}</p>
                    {reattemptError?.groupKey === expandedGroup.groupKey && (
                      <p className="vms-reattempt-error" role="alert">{reattemptError.message}</p>
                    )}
                  </div>
                  {expandedGroup.feedbackAccess.available && expandedGroup.feedbackAccess.launchAccess && (
                    <button type="button" disabled={startingReattemptKey === expandedGroup.groupKey}
                      onClick={() => launchReattempt(expandedGroup)}>
                      {startingReattemptKey === expandedGroup.groupKey ? "Starting reattempt..." : "Reattempt Assessment"}
                    </button>
                  )}
                </section>
              )}

              {expandedGroup.feedbackAccess.status === "released" &&
                expandedGroup.feedbackAccess.mode !== "disabled" && (
                  <section className="vms-feedback-release" role="status">
                    <CheckCircle2 size={19} />
                    <div>
                      <strong>{expandedGroup.feedbackAccess.reason === "requirement-met"
                        ? "Assessment requirement met"
                        : expandedGroup.feedbackAccess.reason === "attempts-exhausted"
                          ? "No reattempts remaining"
                          : "Detailed feedback available"}</strong>
                      <span>{expandedGroup.feedbackAccess.reason === "attempts-exhausted"
                        ? "You have completed all permitted attempts. Your detailed feedback is now available."
                        : "Detailed feedback available."}</span>
                    </div>
                  </section>
                )}

              {expandedGroup.feedbackAccess.status === "released" && <>
                <div className="vms-viewing-attempt">
                  <span>Viewing</span><strong>{selectedAttempt ? attemptLabel(selectedAttempt.attemptNumber) : "Attempt details"}</strong>
                  {selectedAttempt?.outcome?.status !== "complete" && <em>{selectedAttempt?.status === "unavailable" ? "Status unavailable" : selectedAttempt?.status === "in_progress" ? "In progress" : "Marking in progress"}</em>}
                </div>
                <div className="question-panels">
                {(selectedAttempt?.records || []).map((submission, index) => {
                  const questionKey = `${expandedQuizCode}-${submission.id}`;
                  const isExpanded = Boolean(expandedQuestions[questionKey]);
                  const isPending = submission.markAwarded === "Pending";
                  const isFullMarks = !isPending && Number(submission.markAwarded) === Number(submission.maxMark);
                  const panelClass = isPending ? "panel-pending" : isFullMarks ? "panel-full-marks" : "panel-partial-marks";
                  return (
                    <article key={questionKey} className={`question-panel ${panelClass}`}>
                      <button type="button" className="panel-header" onClick={() => toggleQuestion(questionKey)} aria-expanded={isExpanded}>
                        <span className="panel-question-preview"><span className="question-number">Q{index + 1}</span><span>{submission.questionText.length > 100 ? `${submission.questionText.substring(0, 100)}…` : submission.questionText}</span>{(submission.questionImageURL || submission.questionMedia) && <ImageIcon size={15} aria-label="Question has an image" />}</span>
                        <span className="panel-header-right"><span className={`mark-badge ${isPending ? "pending" : isFullMarks ? "full" : Number(submission.markAwarded) > 0 ? "partial" : "zero"}`}>{isPending ? "Pending" : `${submission.markAwarded}/${submission.maxMark}`}</span>{isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
                      </button>

                      {isExpanded && (
                        <div className="panel-details">
                          <section className="detail-section question-section"><h3 className="section-title">Question</h3><div className="section-content">{submission.questionText !== "Unknown Question" && <div className="question-text">{submission.questionText}</div>}{(submission.questionImageURL || submission.questionMedia) && <button type="button" className="question-image-container" aria-label="Open question image"><QuestionImage question={submission} quizId={submission.quizId || expandedGroup?.quizId} context={{ type: "student" }} alt="Question reference" className="question-image" onImageClick={setModalImage} /></button>}</div></section>
                           <section className="detail-section answer-section">
                             <h3 className="section-title">Your Answer</h3>
                             <div className="section-content">
                               {isMultipleChoiceResult(submission)
                                 ? <McqResultSummary mcqResult={submission.mcqResult} showCorrect={!isPending} />
                                 : <><div className="user-answer">{submission.userAnswer}</div><SubmittedDrawingAnswer result={submission} questionText={submission.questionText} typedAnswer={submission.userAnswer}
                                   mark={submission.markAwarded} maxMark={submission.maxMark} feedback={submission.feedback}
                                   questionLabel={`Question ${index + 1}`} /></>}
                             </div>
                           </section>
                           <section className="detail-section feedback-section"><h3 className="section-title">{isMultipleChoiceResult(submission) ? "Feedback" : "AI Feedback"}</h3><div className="section-content feedback-content">{submission.feedback !== "Pending" ? renderFeedback(submission.feedback) : "Pending"}</div></section>

                           {!isMultipleChoiceResult(submission) && submission.feedback !== "Pending" && submission.markAwarded !== "Pending" && (
                            <div className="collapsible-sections-container">
                              <div className="collapsible-section">
                                <button type="button" className={`collapsible-toggle ${expandedSections[`${questionKey}-scheme`] ? "open" : ""}`} onClick={() => toggleSection(`${questionKey}-scheme`)}><span>Mark scheme / full-mark guidance</span>{expandedSections[`${questionKey}-scheme`] ? <ChevronDown size={17} /> : <ChevronRight size={17} />}</button>
                                {expandedSections[`${questionKey}-scheme`] && <div className="collapsible-content mark-scheme">{submission.studentModelAnswer || submission.modelAnswer || submission.markSchemeAnswer}</div>}
                              </div>
                              {submission.modelAnswer && (
                                <div className="collapsible-section">
                                  <button type="button" className={`collapsible-toggle model-answer-toggle ${expandedSections[`${questionKey}-model`] ? "open" : ""}`} onClick={() => toggleSection(`${questionKey}-model`)}><span>Example full-mark answer</span>{expandedSections[`${questionKey}-model`] ? <ChevronDown size={17} /> : <ChevronRight size={17} />}</button>
                                  {expandedSections[`${questionKey}-model`] && <div className="collapsible-content model-answer-content">{submission.modelAnswer}</div>}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="submission-info">Submitted {submission.submittedAt.toLocaleDateString()} at {submission.submittedAt.toLocaleTimeString()}</div>
                        </div>
                      )}
                    </article>
                  );
                })}
                {!selectedAttempt?.records?.length && (
                  <div className="vms-attempt-empty"><Clock3 size={22} /><strong>{selectedAttempt?.status === "unavailable" ? "We could not confirm this attempt's status. Your saved answers are kept." : selectedAttempt?.status === "in_progress" ? "This attempt is in progress" : "Marking in progress"}</strong><span>Question feedback will appear here when it is available.</span></div>
                )}
                </div>
              </>}
            </section>
          )}

          <section className="vms-learning-card"><span className="vms-learning-icon"><BookOpen size={25} /></span><div><h2>Turn feedback into progress</h2><p>Revisit your learning materials before your next assessment.</p></div><button type="button" onClick={() => navigate("/my-assessments")}>Open Learning Hub</button></section>
        </main>
        <footer className="vms-footer">© {new Date().getFullYear()} StudyDen. All rights reserved.</footer>
      </div>

      {modalImage && (
        <div className="image-modal" role="dialog" aria-modal="true" aria-label="Question image preview" onClick={() => setModalImage(null)}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="close-modal" onClick={() => setModalImage(null)} aria-label="Close image preview">×</button>
            <img src={modalImage} alt="Enlarged question" className="modal-image" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewMyScores;
